/**
 * Section width: how much of the page's width a section takes, on the same
 * 12-column grid as widgets. Sections whose widths fit together sit side by
 * side (a comparison of two countries, two variants…); a full-width section
 * starts a new row. Like widget widths, it applies from the `xl` breakpoint;
 * narrower screens stack the sections.
 *
 * Stored on the section's composition block as `width` (1–12); absent is the
 * full width, so existing dashboards keep their stacked sections.
 */

// Written out in full so the stylesheet keeps every class (same steps as widget widths).
const SPAN_CLASSES: Record<number, string> = {
  1: "col-span-12 xl:col-span-1",
  2: "col-span-12 xl:col-span-2",
  3: "col-span-12 xl:col-span-3",
  4: "col-span-12 xl:col-span-4",
  5: "col-span-12 xl:col-span-5",
  6: "col-span-12 xl:col-span-6",
  7: "col-span-12 xl:col-span-7",
  8: "col-span-12 xl:col-span-8",
  9: "col-span-12 xl:col-span-9",
  10: "col-span-12 xl:col-span-10",
  11: "col-span-12 xl:col-span-11",
  12: "col-span-12",
};

export const FULL_SECTION_WIDTH = 12;

/** The widths offered to authors, in the order they are listed. */
export const SECTION_WIDTH_OPTIONS = [
  { value: 12, label: "Full width", short: "Full" },
  { value: 8, label: "Two thirds", short: "⅔" },
  { value: 6, label: "Half", short: "½" },
  { value: 4, label: "One third", short: "⅓" },
] as const;

interface WidthBlock {
  width?: number;
}

export function isValidSectionWidth(width: unknown): width is number {
  return typeof width === "number" && Number.isInteger(width) && width >= 1 && width <= 12;
}

export function sectionWidth(block: WidthBlock | undefined): number {
  return isValidSectionWidth(block?.width) ? block.width : FULL_SECTION_WIDTH;
}

/** The grid classes of a section: its width from `xl`, the full row below. */
export function sectionSpanClassName(block: WidthBlock | undefined): string {
  return SPAN_CLASSES[sectionWidth(block)];
}

/** The block with a new width; the full width is the default and is not stored. */
export function withSectionWidth<T extends object>(block: T, width: number): T {
  const { width: _width, ...rest } = block as T & WidthBlock;
  return (
    width === FULL_SECTION_WIDTH || !isValidSectionWidth(width) ? rest : { ...rest, width }
  ) as T;
}

/** A short label for a width ("½"), or `n/12` for a width set outside the offered steps. */
export function sectionWidthShortLabel(width: number): string {
  return SECTION_WIDTH_OPTIONS.find((option) => option.value === width)?.short ?? `${width}/12`;
}

/**
 * How a section stands apart from its neighbours (`blocks[].frame`):
 * - `none` (absent): space alone — sections side by side keep twice the widget
 *   gap between them;
 * - `line`: a hairline drawn in the gap before the section, beside it or above
 *   it; at the page's edge the page clips it, so it only ever separates;
 * - `panel`: its title and widgets on a framed surface.
 */
export const SECTION_FRAME_OPTIONS = [
  { value: "none", label: "Space" },
  { value: "line", label: "Line" },
  { value: "panel", label: "Panel" },
] as const;

export type SectionFrameValue = (typeof SECTION_FRAME_OPTIONS)[number]["value"];

export function sectionFrame(block: { frame?: string } | undefined): SectionFrameValue {
  return block?.frame === "line" || block?.frame === "panel" ? block.frame : "none";
}

/** The block with a new frame; space alone is the default and is not stored. */
export function withSectionFrame<T extends object>(block: T, frame: SectionFrameValue): T {
  const { frame: _frame, ...rest } = block as T & { frame?: string };
  return (frame === "none" ? rest : { ...rest, frame }) as T;
}

/** The page grid holding sections: wider gaps between columns than between widgets. */
export const SECTION_GRID_CLASS =
  "dashboard-sections grid grid-cols-12 gap-y-(--section-gap-y) gap-x-(--section-gap-x)";
